import { HttpErrorResponse } from '@angular/common/http';

import { createSelectorFactory, defaultMemoize, MemoizedSelector } from '@ngrx/store';

import { Candidate, CandidateRating, Placement } from '@recruitee/http/types';
import {
  createEntityCollectionStore,
  getEntityCollectionActions,
  memoizeSelector,
  ngrxEvent,
  PickActions,
  pipeState,
  Reference,
} from '@recruitee/ngrx';
import { PlacementSelectors } from '@recruitee/placements/data';

import { EntityStoreKey } from './../types';
import { CandidateCollectionParams } from './candidate-http-client';

const idSelector = (candidate: { id: number }) => candidate.id;
const { actions, selectors, reducer, namespace } = createEntityCollectionStore<Candidate>()(
  EntityStoreKey.candidate,
  {
    idSelector,
    references: makeRef =>
      <Array<Reference<Candidate>>>[
        makeRef(
          'placements',
          'placements',
          (placements: Placement[]): ((store: any) => Placement[]) =>
            PlacementSelectors.byIdsWithReferences((placements || []).map(({ id }) => id)),
        ),
      ],
    customActions: adapter => {
      const defaultActions = getEntityCollectionActions(adapter, idSelector);
      return {
        references: (state, payload: { data: Candidate[] }) =>
          adapter.updateOrCreateMany(state, payload.data),

        fetchCollection: (
          state,
          {
            scope,
            params,
            clear,
          }: {
            scope: string;
            params: CandidateCollectionParams;
            clear?: boolean;
          },
        ) =>
          pipeState(
            state,
            next => (clear ? adapter.resetScope(next, scope) : next),
            next => defaultActions.fetchCollection(next, { scope }),
          ),

        fetchCandidateRating: (state, payload: { id: number }) =>
          adapter.markAsPending(state, { entities: [payload.id], scope: null }),
        fetchCandidateRatingSuccess: (state, payload: CandidateRating) =>
          pipeState(
            state,
            next => adapter.updateOne(next, payload),
            next => adapter.markAsLoaded(next, { entities: [payload.id], scope: null }),
          ),
        fetchCandidateRatingFailed: ngrxEvent<{ error: HttpErrorResponse }>(),
      };
    },
  },
);

const byIds = memoizeSelector(
  (candidateIds: number[]): MemoizedSelector<any, any> =>
    createSelectorFactory(defaultMemoize)(
      ...candidateIds.map(candidateId => selectors.byId(candidateId)!),
      (...result: Candidate[]) => result,
    ),
);

const CandidateActions: Pick<typeof actions, 'references'> &
  PickActions<typeof actions, 'fetchCollection' | 'fetchCandidateRating'> = actions;

const CandidateSelectors = {
  ...selectors,
  byIds,
};

export {
  CandidateActions,
  CandidateSelectors,
  namespace as candidateNamespace,
  reducer as CandidateReducer,
};
